import { useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { workspaceSharingService } from "@/services/workspaceSharingService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowRight,
  Share2,
  Lock,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { buildLoginUrlWithReturnTo } from "@/lib/publicRoutes";

export default function WorkspaceShareAcceptance() {
  const [, params] = useRoute("/workspaces/share/:token");
  const [location, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const { setCurrentWorkspaceId, refetchWorkspaces } = useWorkspace();
  const queryClient = useQueryClient();
  const token = params?.token || "";

  const [hasResponded, setHasResponded] = useState(false);

  const { data: validation, isLoading: isValidating } = useQuery({
    queryKey: ["/api/workspaces/share/validate", token],
    queryFn: async () => {
      if (!token) return null;
      return await workspaceSharingService.validateShareLink(token);
    },
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      return await workspaceSharingService.acceptShareInvitation(token);
    },
    onSuccess: async () => {
      setHasResponded(true);
      toast({
        title: "Access granted",
        description: "You now have access to this workspace",
      });
      queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      refetchWorkspaces();
      const v = await workspaceSharingService.validateShareLink(token);
      setTimeout(() => {
        if (v.workspace) {
          setCurrentWorkspaceId(v.workspace.id);
          setLocation("/");
        } else {
          setLocation("/workspaces");
        }
      }, 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to accept",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async () => {
      return await workspaceSharingService.rejectShareInvitation(token);
    },
    onSuccess: () => {
      setHasResponded(true);
      toast({
        title: "Invitation declined",
        description: "You have declined access to this workspace",
      });
      setTimeout(() => setLocation("/workspaces"), 1500);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to reject",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  if (authLoading || isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Validating share link...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Sign in required
            </CardTitle>
            <CardDescription>You need to sign in to accept this workspace invitation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Authentication required</AlertTitle>
              <AlertDescription>
                Please sign in or create an account to accept this workspace invitation.
              </AlertDescription>
            </Alert>
            <Button onClick={() => setLocation(buildLoginUrlWithReturnTo(location))} className="w-full" data-testid="button-go-to-login">
              Go to sign in
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!validation || !validation.isValid) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Invalid link
            </CardTitle>
            <CardDescription>This share link is not valid or has been deactivated</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Link not valid</AlertTitle>
              <AlertDescription>
                {validation?.message ||
                  "The share link you're trying to access is invalid, expired, or has been revoked."}
              </AlertDescription>
            </Alert>
            <Button onClick={() => setLocation("/workspaces")} variant="outline" className="w-full">
              Go to workspaces
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { share, workspace, access } = validation;

  const isCreator = Boolean(workspace && share && user.id === share.createdByUserId);

  if (access?.status === "accepted" || hasResponded) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Access granted
            </CardTitle>
            <CardDescription>
              {hasResponded ? "Redirecting..." : "You already have access to this workspace"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>You have access</AlertTitle>
              <AlertDescription>
                {isCreator
                  ? "This workspace belongs to you. Redirecting..."
                  : "You have already accepted access to this workspace. Redirecting..."}
              </AlertDescription>
            </Alert>
            {workspace && (
              <Button
                onClick={() => {
                  setCurrentWorkspaceId(workspace.id);
                  setLocation("/");
                }}
                className="w-full"
              >
                Open workspace
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (access?.status === "rejected") {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Access declined
            </CardTitle>
            <CardDescription>You have previously declined access to this workspace</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Access declined</AlertTitle>
              <AlertDescription>
                You previously declined the invitation to access this workspace.
              </AlertDescription>
            </Alert>
            <Button onClick={() => setLocation("/workspaces")} variant="outline" className="w-full">
              Go to workspaces
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Workspace invitation
          </CardTitle>
          <CardDescription>You&apos;ve been invited to join a workspace</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {workspace && (
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{workspace.name}</h3>
                  {workspace.slug && (
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{workspace.slug}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Accept invitation?</AlertTitle>
            <AlertDescription>
              Accepting will add you to this workspace with the role chosen by the inviter (editor or viewer).
            </AlertDescription>
          </Alert>

          {share?.expiresAt && (
            <p className="text-xs text-muted-foreground text-center">
              Link expires {formatDistanceToNow(new Date(share.expiresAt), { addSuffix: true })}
            </p>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => rejectMutation.mutate()}
              disabled={acceptMutation.isPending || rejectMutation.isPending || hasResponded}
              className="flex-1"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Decline
            </Button>
            <Button
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending || rejectMutation.isPending || hasResponded}
              className="flex-1"
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Accepting...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Accept
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
